import { MarkerType, type Node, Node as ReactFlowNode, type Edge as ReactFlowEdge } from '@xyflow/react'
import { t } from 'i18next'
import { chain, filter, find, get, isEmpty, map, some } from 'lodash'
import { store } from '@/stores'

import { LabelingNodeInfo } from '@/api/LabelingNode/types'
import { ModelNodeInfo } from '@/api/ModelNode/types'

import { GenericNode, NodeTypes } from '@/pages/RenewedPipeline/Nodes/types'
import { MODEL_TYPES, RUNNING_TASKS, type ModelValueType, type TaskStatusValueType } from '@/pages/const'
import { ValidationNodeInfo } from '@/api/ValidationNode/types'
import { InferenceNodeInfo } from '@/api/InferenceNode/types'
import { DATASET_CATEGORY, DatasetNodeInfo } from '@/api/DatasetNode/types'
import { ImageMatchingNodeInfo } from '@/api/ImageMatchingNode/types'
import { PreprocessingNodeInfo } from '@/api/PreprocessingNode/types'
import { PreprocessingResultNodeInfo } from '@/api/PreprocessingResultNode/types'
import { RoiNodeInfo } from '@/api/RoiNode/types'
import { DataDriftNodeInfo } from '@/api/DataDrift/types'
import { ModelDriftNodeInfo } from '@/api/ModelDrift/types'
import { ModelPerformanceNodeInfo } from '@/api/ModelPerformance/types'
import { NODE_TYPES } from '@/pages/RenewedPipeline/Nodes/const'
import { Edge, PROJECT_NODE_TYPE } from '@/api/Project/types'
import { EdaNodeInfo } from '@/api/Tabular/types'
import { ExternalModelNodeInfo } from '@/api/ExternalModel/types'
import { ModelPipelineMergeNodeInfo } from '@/api/MergeNode/types'

export const getModelTypeText = (modelType: ModelValueType | null | undefined | string) => {
  if (modelType === MODEL_TYPES.ANOMALY_DETECTION) {
    return t('common_anomalyDetection')
  }
  if (modelType === MODEL_TYPES.SEGMENTATION) {
    return t('common_segmentation')
  }
  if (modelType === MODEL_TYPES.CLASSIFICATION) {
    return t('common_classification')
  }
  if (modelType === MODEL_TYPES.OBJECT_DETECTION) {
    return t('common_objectDetection')
  }
  if (modelType === MODEL_TYPES.IMAGE_ENHANCEMENT) {
    return t('common_imageEnhancement')
  }
  if (modelType === MODEL_TYPES.OCR) {
    return t('common_ocr')
  }
  if (modelType === MODEL_TYPES.REGRESSION_TABULAR) {
    return t('common_regression')
  }
  if (modelType === 'OD-AD-CLS') {
    return 'OD → AD → CLS'
  }
  if (modelType === 'OD-SEG') {
    return 'OD → SEG'
  }
  return '-'
}

export const transformNodes = (type: NodeTypes, nodes: (GenericNode | undefined)[]): ReactFlowNode[] => {
  const filtered = filter(nodes, (n) => n !== undefined) as GenericNode[]
  return map(filtered, (n) => ({
    id: n._id,
    type,
    position: get(n, 'metadata.position', { x: 0, y: 0 }),
    data: n,
  }))
}

const getEdges = (node: Node, connnectedIds: string[] | null | undefined, metadata: any) => map(connnectedIds, (id, index) => ({
  id: `${id}-${node.id}:${index}`,
  source: id,
  target: node.id,
  type: 'customEdge',
  data: { points: filter(metadata.points, (p) => p.id.includes(id)) },
  markerEnd: { type: MarkerType.Arrow },
}))

export const makeNewEdge = (
  sourceNodeId: string,
  sourceNodeType: NodeTypes,
  targetNodeType: NodeTypes,
  targetNodeId = '',
  targetHandle = ''
): Edge => {
  const targetHandleMap: Record<NodeTypes, string> = {
    [NODE_TYPES.DATASET]: 'dataset',
    [NODE_TYPES.LABELING]: 'labeling',
    [NODE_TYPES.TRAIN]: 'train',
    [NODE_TYPES.VALIDATION]: sourceNodeType === NODE_TYPES.TRAIN ? 'validation-train' : 'validation',
    [NODE_TYPES.INFERENCE]: sourceNodeType === NODE_TYPES.TRAIN || sourceNodeType === NODE_TYPES.EXTERNAL_MODEL ? 'inference-train' : 'inference',
    [NODE_TYPES.PREPROCESSING]: 'preprocessing',
    [NODE_TYPES.PREPROCESSING_RESULT]: 'preprocessingResult',
    [NODE_TYPES.ROI]: 'roi',
    [NODE_TYPES.IMAGE_MATCHING]: 'imageMatching',
    [NODE_TYPES.DATA_DRIFT]: targetHandle || 'dataDrift-reference',
    [NODE_TYPES.MODEL_DRIFT]: targetHandle || 'modelDrift-reference',
    [NODE_TYPES.MODEL_PERFORMANCE]: 'modelPerformance',
    [NODE_TYPES.MODEL_RETRAIN]: 'modelRetrain', // FIXME: 확인 필요
    [NODE_TYPES.MODEL_RETRAIN_EVALUATION]: 'modelRetrainEvaluation', // FIXME: 확인 필요
    [NODE_TYPES.MODEL_PIPELINE_SOURCE]: 'modelPipelineSource',
    [NODE_TYPES.MODEL_PIPELINE_INSTANCE]: 'modelPipelineInstance',
    [NODE_TYPES.MODEL_PIPELINE_GATE]: 'modelPipelineGate',
    [NODE_TYPES.MODEL_PIPELINE_MERGE]: 'modelPipelineMerge',
    [NODE_TYPES.MODEL_PIPELINE_RESULT]: 'modelPipelineResult',
    [NODE_TYPES.GROUP]: 'group',
    [NODE_TYPES.DATA_STREAMING]: 'dataStreaming',
    [NODE_TYPES.EDA]: 'eda',
    [NODE_TYPES.EXTERNAL_MODEL]: 'externalModel',
  }
  return {
    id: targetNodeId ? `${sourceNodeId}-${targetNodeId}` : '',
    source: sourceNodeId,
    target: targetNodeId,
    sourceHandle: sourceNodeType, // 추가되어야함
    targetHandle: targetHandleMap[targetNodeType],
    type: 'customEdge',
    data: {},
    markerEnd: { type: MarkerType.Arrow },
  }
}

export const validateHandleTypes = (sourceHandle: string, targetHandle: string) => {
  if (sourceHandle === 'dataset') {
    return targetHandle === 'labeling' || targetHandle === 'inference' || targetHandle === 'imageMatching' || targetHandle === 'preprocessing' || targetHandle === 'modelDrift' || targetHandle === 'modelDrift-reference'
  }
  if (sourceHandle === 'labeling') {
    return targetHandle === 'train' || targetHandle === 'validation' || targetHandle === 'roi' || targetHandle === 'dataDrift' || targetHandle === 'dataDrift-reference'
  }
  if (sourceHandle === 'train') {
    return targetHandle === 'validation-train' || targetHandle === 'inference-train'
  }
  if (sourceHandle === 'imageMatching') {
    return targetHandle === 'train' || targetHandle === 'validation'
  }
  if (sourceHandle === 'preprocessing') {
    return targetHandle === 'preprocessingResult'
  }
  if (sourceHandle === 'preprocessingResult') {
    return targetHandle === 'labeling' || targetHandle === 'inference' || targetHandle === 'imageMatching' || targetHandle === 'preprocessing'
  }
  if (sourceHandle === 'roi') {
    return targetHandle === 'train' || targetHandle === 'validation'
  }
  if (sourceHandle === 'validation') {
    return targetHandle === 'modelPerformance'
  }
  if (sourceHandle === 'dataStreaming') {
    return targetHandle === 'dataDrift' || 'dataDrift-reference' || targetHandle === 'modelDrift' || targetHandle === 'modelDrift-reference'
  }
  if (sourceHandle === 'eda') {
    return targetHandle === 'labeling'
  }
  if (sourceHandle === 'externalModel') {
    return targetHandle === 'inference-train'
  }
  if (targetHandle === 'modelPipelineMerge') {
    return sourceHandle === 'modelPipelineGate' || sourceHandle === 'modelPipelineInstance'
  }
  return false
}

const ERROR_ALERTS = {
  ALREADY_CONNECTED_TOAST: { title: '', message: 'pipeline_error_nodeLink_duplicateConnection', type: 'toast', shouldDisconnect: false },
  CONFLICTING_LINKS_DIALOG: { title: 'pipeline_error_nodeLink_replaceNodes', message: 'common_dataLostWarning', type: 'dialog', shouldDisconnect: true },
  DIFFERENT_CLASS_GROUP_DIALOG: { title: 'pipeline_error_nodeLink_differentClassGroups', message: 'pipeline_error_nodeLink_replaceNodes', type: 'dialog', shouldDisconnect: true },
  DIFFERENT_LABELING_TYPE_DIALOG: { title: 'pipeline_error_nodeLink_differentLabelingTypes', message: 'pipeline_error_nodeLink_replaceNodes', type: 'dialog', shouldDisconnect: true },
  NO_CLS_DIALOG: { title: 'pipeline_error_nodeLink_invalidLabelingType', message: 'pipeline_error_nodeLink_noClsAllowed', type: 'dialog', shouldDisconnect: false },
  INVALID_TYPE_TOAST: { title: 'pipeline_error_nodeLink_title', message: 'pipeline_error_nodeLink_invalidType', type: 'toast', shouldDisconnect: false },
  IMAGE_ENHANCEMENT_DIALOG: { title: 'modelDrift_toast_incompatibleNode_title', message: 'modelDrift_toast_incompatibleNode_message', type: 'toast', shouldDisconnect: false },
  EDA_HAS_MISSING_DATA: { title: 'pipeline_error_nodeLink_edaHasMissingData', message: '', type: 'toast', shouldDisconnect: false },
  INVALID_DATASET_CATEGORY_TOAST: { title: 'pipeline_error_nodeLink_title', message: 'pipeline_error_nodeLink_invalidDatasetCategory', type: 'toast', shouldDisconnect: false },
}

/** returns true if valid, object with error title & message if invalid */
export const validateNodeConnection = (sourceNodeId: string, targetNodeType: NodeTypes, nodeList: Node[], edges: ReactFlowEdge[], targetNodeId?: string) => {
  const sourceNode = find(nodeList, { id: sourceNodeId })
  const targetNode = find(nodeList, { id: targetNodeId })
  const connectedEdges = filter(edges, (e) => e.source === sourceNodeId)
  const connectedNodeIds = map(connectedEdges, (e) => e.target)

  if (connectedNodeIds.includes(get(targetNode, 'id', ''))) {
    return ERROR_ALERTS.ALREADY_CONNECTED_TOAST
  }
  const targetSourceNodes = get(targetNode, 'data.sourceNodes', [])
  if (
    !isEmpty(targetSourceNodes)
    && get(sourceNode, 'data.datasetCategory', null) !== null
    && get(targetNode, 'data.datasetCategory', null) !== null
  ) {
    if (get(sourceNode, 'data.datasetCategory') !== get(targetNode, 'data.datasetCategory')) {
      return ERROR_ALERTS.INVALID_DATASET_CATEGORY_TOAST
    }
  }

  if (isDatasetNode(sourceNode)) {
    if (isPreprocessingNode(targetNode)) {
      const { data: preprocessingNodeInfo } = targetNode
      const sourceNodes = get(preprocessingNodeInfo, 'sourceNodes', [])
      if (!isEmpty(sourceNodes)) {
        return ERROR_ALERTS.ALREADY_CONNECTED_TOAST
      }
    }
    if (isInferenceNode(targetNode)) {
      const { data: inferenceNodeInfo } = targetNode
      const sourceNodes = get(inferenceNodeInfo, 'sourceNodes', [])
      const sourceDatasetNodes = filter(sourceNodes, (n) => n.nodeType !== PROJECT_NODE_TYPE.MODEL_TRAINING)
      if (get(sourceNode, 'data.datasetCategory') === DATASET_CATEGORY.TABULAR && !isEmpty(sourceDatasetNodes)) {
        return ERROR_ALERTS.ALREADY_CONNECTED_TOAST
      }
    }
    if (isImageMatchingNode(targetNode) || isLabelingNode(targetNode)) {
      if (get(sourceNode, 'data.datasetCategory') === DATASET_CATEGORY.TABULAR) {
        return ERROR_ALERTS.INVALID_TYPE_TOAST
      }
    }
    if (isImageMatchingNode(targetNode) || isPreprocessingNode(targetNode)) {
      if (get(sourceNode, 'data.datasetCategory') === DATASET_CATEGORY.VIDEO) {
        return ERROR_ALERTS.INVALID_TYPE_TOAST
      }
    }
  }

  if (isEdaNode(sourceNode)) {
    const { data: edaNodeInfo } = sourceNode
    const branchNodes = get(edaNodeInfo, 'branchNodes', [])
    if (!isEmpty(branchNodes)) {
      return ERROR_ALERTS.ALREADY_CONNECTED_TOAST
    }
    if (edaNodeInfo.hasMissingCount) {
      return ERROR_ALERTS.EDA_HAS_MISSING_DATA
    }
    if (isLabelingNode(targetNode)) {
      const { data: labelingNodeInfo } = targetNode
      const sourceNodes = get(labelingNodeInfo, 'sourceNodes', [])
      if (!isEmpty(sourceNodes)) {
        return ERROR_ALERTS.ALREADY_CONNECTED_TOAST
      }
    }
  }

  if (isLabelingNode(sourceNode)) {
    const { data: labelingNodeInfo } = sourceNode
    if (isModelNode(targetNode)) {
      const { data: modelNodeInfo } = targetNode
      const sourceNodes = get(modelNodeInfo, 'sourceNodes', [])
      const { labelingType, modelType } = modelNodeInfo
      if ((get(modelNodeInfo, 'datasetCategory') === DATASET_CATEGORY.TABULAR || get(labelingNodeInfo, 'datasetCategory') === DATASET_CATEGORY.TABULAR) && !isEmpty(sourceNodes)) {
        return ERROR_ALERTS.ALREADY_CONNECTED_TOAST
      }
      if (modelNodeInfo.classGroupId && modelNodeInfo.classGroupId !== labelingNodeInfo.classGroupId) {
        return ERROR_ALERTS.DIFFERENT_CLASS_GROUP_DIALOG
      }
      if ((labelingType && labelingType !== labelingNodeInfo.modelType) || (modelType && modelType !== labelingNodeInfo.modelType) || modelType === MODEL_TYPES.IMAGE_ENHANCEMENT) {
        return ERROR_ALERTS.DIFFERENT_LABELING_TYPE_DIALOG
      }
      const sourceRoiNodes = filter(sourceNodes, (n) => n.nodeType === PROJECT_NODE_TYPE.ROI)
      const sourceImageMatchingNodes = filter(sourceNodes, (n) => n.nodeType === PROJECT_NODE_TYPE.IMAGE_MATCHING)
      if (!isEmpty(sourceRoiNodes) || !isEmpty(sourceImageMatchingNodes)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }

    if (isValidationNode(targetNode)) {
      const { data: validationNodeInfo } = targetNode
      const labelingType = get(validationNodeInfo, 'labelingType', '')
      const modelType = get(validationNodeInfo, 'modelType', '')
      const classGroupId = get(validationNodeInfo, 'classGroupId', '')
      const sourceNodes = get(validationNodeInfo, 'sourceNodes', [])
      const sourceLabelingNodes = filter(sourceNodes, (n) => n.nodeType === PROJECT_NODE_TYPE.LABELING)
      if (get(sourceNode, 'datasetCategory') === DATASET_CATEGORY.TABULAR && !isEmpty(sourceLabelingNodes)) {
        return ERROR_ALERTS.ALREADY_CONNECTED_TOAST
      }
      if (labelingNodeInfo?.tabularType !== null && validationNodeInfo?.tabularType !== null && labelingNodeInfo.tabularType !== validationNodeInfo.tabularType) {
        return ERROR_ALERTS.INVALID_TYPE_TOAST
      }
      if (modelType !== MODEL_TYPES.REGRESSION_TABULAR && classGroupId && classGroupId !== labelingNodeInfo.classGroupId) {
        return ERROR_ALERTS.DIFFERENT_CLASS_GROUP_DIALOG
      }
      if ((labelingType && labelingType !== labelingNodeInfo.modelType) || (modelType && modelType !== labelingNodeInfo.modelType) || labelingType === MODEL_TYPES.IMAGE_ENHANCEMENT) {
        return ERROR_ALERTS.DIFFERENT_LABELING_TYPE_DIALOG
      }
      const sourceRoiNodes = filter(sourceNodes, (n) => n.nodeType === PROJECT_NODE_TYPE.ROI)
      const sourceImageMatchingNodes = filter(sourceNodes, (n) => n.nodeType === PROJECT_NODE_TYPE.IMAGE_MATCHING)
      if (!isEmpty(sourceRoiNodes) || !isEmpty(sourceImageMatchingNodes)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }
    if (isRoiNode(targetNode)) {
      const { data: roiNodeInfo } = targetNode
      const labelingType = get(roiNodeInfo, 'labelingType', '')
      const classGroupId = get(roiNodeInfo, 'classGroupId', '')
      if (get(labelingNodeInfo, 'datasetCategory') === DATASET_CATEGORY.TABULAR) {
        return ERROR_ALERTS.INVALID_TYPE_TOAST
      }
      if (classGroupId && classGroupId !== labelingNodeInfo.classGroupId) {
        return ERROR_ALERTS.DIFFERENT_CLASS_GROUP_DIALOG
      }
      if ((labelingType && labelingType !== labelingNodeInfo.modelType) || labelingType === MODEL_TYPES.IMAGE_ENHANCEMENT) {
        return ERROR_ALERTS.DIFFERENT_LABELING_TYPE_DIALOG
      }
    }
    if (isDataDriftNode(targetNode)) {
      if (labelingNodeInfo.modelType === MODEL_TYPES.CLASSIFICATION) {
        return ERROR_ALERTS.NO_CLS_DIALOG
      }
    }
  }

  if (isRoiNode(sourceNode)) {
    const { data: roiNodeInfo } = sourceNode
    if (isModelNode(targetNode)) {
      const { data: modelNodeInfo } = targetNode
      const sourceNodes = get(modelNodeInfo, 'sourceNodes', [])
      if (get(modelNodeInfo, 'datasetCategory') === DATASET_CATEGORY.TABULAR) {
        return ERROR_ALERTS.INVALID_TYPE_TOAST
      }
      if (modelNodeInfo.classGroupId && modelNodeInfo.classGroupId !== roiNodeInfo.classGroupId) {
        return ERROR_ALERTS.DIFFERENT_CLASS_GROUP_DIALOG
      }
      if ((modelNodeInfo.modelType && modelNodeInfo.modelType !== roiNodeInfo.labelingType) || modelNodeInfo.modelType === MODEL_TYPES.IMAGE_ENHANCEMENT) {
        return ERROR_ALERTS.DIFFERENT_LABELING_TYPE_DIALOG
      }
      // NOTE: 추후 roi도 N:1 가능하면 로직 수정 필요
      if (!isEmpty(sourceNodes)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }
    if (isValidationNode(targetNode)) {
      const { data: validationNodeInfo } = targetNode
      const labelingType = get(validationNodeInfo, 'labelingType', '')
      const modelType = get(validationNodeInfo, 'modelType', '')
      const classGroupId = get(validationNodeInfo, 'classGroupId', '')
      if (classGroupId && classGroupId !== roiNodeInfo.classGroupId) {
        return ERROR_ALERTS.DIFFERENT_CLASS_GROUP_DIALOG
      }
      if ((labelingType && labelingType !== roiNodeInfo.labelingType) || (modelType && modelType !== roiNodeInfo.labelingType) || labelingType === MODEL_TYPES.IMAGE_ENHANCEMENT) {
        return ERROR_ALERTS.DIFFERENT_LABELING_TYPE_DIALOG
      }
      const sourceNodes = filter(get(validationNodeInfo, 'sourceNodes', []), (n) => n.nodeType !== PROJECT_NODE_TYPE.MODEL_TRAINING)
      // NOTE: 추후 roi도 N:1 가능하면 로직 수정 필요
      if (!isEmpty(sourceNodes)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }
  }

  if (isModelNode(sourceNode)) {
    const { data } = sourceNode
    if (isValidationNode(targetNode)) {
      const { data: validationNodeInfo } = targetNode
      const labelingType = get(validationNodeInfo, 'labelingType', '')
      const modelType = get(validationNodeInfo, 'modelType', '')
      const classGroupId = get(validationNodeInfo, 'classGroupId', '')

      if (data?.tabularType !== null && validationNodeInfo?.tabularType !== null && data.tabularType !== validationNodeInfo.tabularType) {
        return ERROR_ALERTS.INVALID_TYPE_TOAST
      }
      if ((labelingType && labelingType !== data.modelType) || (modelType && modelType !== data.modelType)) {
        return ERROR_ALERTS.DIFFERENT_LABELING_TYPE_DIALOG
      }
      if (classGroupId && data.classGroupId !== classGroupId) {
        return ERROR_ALERTS.DIFFERENT_CLASS_GROUP_DIALOG
      }
    }
    if (isValidationNode(targetNode) || isInferenceNode(targetNode)) {
      const { data } = targetNode
      const sourceModelNode = filter(get(data, 'sourceNodes', []), (n) => n.nodeType === PROJECT_NODE_TYPE.MODEL_TRAINING)
      if (!isEmpty(sourceModelNode)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }
  }

  if (isExternalModelNode(sourceNode)) {
    if (isInferenceNode(targetNode)) {
      const { data } = targetNode
      const sourceModelNode = filter(get(data, 'sourceNodes', []), (n) => n.nodeType === PROJECT_NODE_TYPE.EXTERNAL_MODEL)
      if (!isEmpty(sourceModelNode)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }
  }

  if (isImageMatchingNode(sourceNode)) {
    if (isModelNode(targetNode)) {
      const { data } = targetNode
      const sourceNodes = get(data, 'sourceNodes', [])
      const diffTypeNodes = filter(sourceNodes, (sourceNode) => sourceNode.nodeType !== PROJECT_NODE_TYPE.IMAGE_MATCHING)
      if (!isEmpty(diffTypeNodes)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }
    if (isValidationNode(targetNode)) {
      const { data } = targetNode
      const sourceNodes = get(data, 'sourceNodes', [])
      const diffTypeNodes = filter(sourceNodes, (sourceNode) => sourceNode.nodeType !== PROJECT_NODE_TYPE.IMAGE_MATCHING && sourceNode.nodeType !== PROJECT_NODE_TYPE.MODEL_TRAINING)
      if (!isEmpty(diffTypeNodes)) {
        return ERROR_ALERTS.CONFLICTING_LINKS_DIALOG
      }
    }
  }

  if (isValidationNode(sourceNode)) {
    if (isModelPerformanceNode(targetNode)) {
      const { data: validationNodeInfo } = sourceNode
      if (validationNodeInfo.modelType === MODEL_TYPES.IMAGE_ENHANCEMENT) {
        return ERROR_ALERTS.IMAGE_ENHANCEMENT_DIALOG
      }
    }
  }

  if (isPreprocessingResultNode(sourceNode)) {
    const { data: preprocessingResultNodeInfo } = sourceNode
    if (isPreprocessingNode(targetNode)) {
      // 재귀적으로 앞쪽 전처리 노드들을 찾아서 targetNode와 비교
      const upstreamPreprocessingNodeIds = findUpstreamPreprocessingNodes(preprocessingResultNodeInfo, nodeList)

      if (upstreamPreprocessingNodeIds.includes(targetNode.id)) {
        return ERROR_ALERTS.INVALID_TYPE_TOAST
      }
    }
    if (isLabelingNode(targetNode)) {
      const { data: labelingNodeInfo } = targetNode
      const datasetCategory = get(labelingNodeInfo, 'datasetCategory', '') // NOTE: 이미지인 경우 null로 오고있음
      if (datasetCategory === DATASET_CATEGORY.TABULAR) {
        return ERROR_ALERTS.INVALID_DATASET_CATEGORY_TOAST
      }
    }
  }
  return true
}

// 전처리 결과 노드에서 재귀적으로 앞쪽 전처리 노드들을 찾는 함수
const findUpstreamPreprocessingNodes = (
  preprocessingResultNodeInfo: PreprocessingResultNodeInfo,
  nodeList: Node[],
  visited: Set<string> = new Set()
): string[] => {
  const upstreamPreprocessingNodeIds: string[] = []

  // 이미 방문한 노드인지 확인
  if (visited.has(preprocessingResultNodeInfo._id)) {
    return upstreamPreprocessingNodeIds
  }

  visited.add(preprocessingResultNodeInfo._id)

  // 현재 결과 노드의 sourceNodes들을 확인
  preprocessingResultNodeInfo.sourceNodes.forEach((sourceNode) => {
    const sourceNodeInList = find(nodeList, { id: sourceNode.nodeId })

    if (sourceNodeInList && isPreprocessingNode(sourceNodeInList)) {
      // 찾은 노드가 전처리노드면 리스트에 바로 추가
      upstreamPreprocessingNodeIds.push(sourceNode.nodeId)

      // 해당 전처리 노드의 sourceNodes도 재귀적으로 확인
      const preprocessingNodeInfo = sourceNodeInList.data as PreprocessingNodeInfo
      if (preprocessingNodeInfo.sourceNodes) {
        preprocessingNodeInfo.sourceNodes.forEach((upstreamSourceNode) => {
          const upstreamSourceNodeInList = find(nodeList, { id: upstreamSourceNode.nodeId })

          if (upstreamSourceNodeInList && isPreprocessingResultNode(upstreamSourceNodeInList)) {
            // 상위 결과 노드를 찾았으면 재귀적으로 더 위쪽 전처리 노드들을 찾기
            const upstreamResultNodeInfo = upstreamSourceNodeInList.data as PreprocessingResultNodeInfo
            const deeperUpstreamIds = findUpstreamPreprocessingNodes(upstreamResultNodeInfo, nodeList, visited)
            upstreamPreprocessingNodeIds.push(...deeperUpstreamIds)
          }
        })
      }
    } else if (sourceNodeInList && isPreprocessingResultNode(sourceNodeInList)) {
      // 상위 결과 노드를 찾았으면 재귀적으로 더 위쪽 전처리 노드들을 찾기
      const upstreamResultNodeInfo = sourceNodeInList.data as PreprocessingResultNodeInfo
      const deeperUpstreamIds = findUpstreamPreprocessingNodes(upstreamResultNodeInfo, nodeList, visited)
      upstreamPreprocessingNodeIds.push(...deeperUpstreamIds)
    }
  })

  return upstreamPreprocessingNodeIds
}

export const isDatasetNode = (node?: Record<string, any>): node is Node<DatasetNodeInfo, 'dataset'> => node?.type === 'dataset'
export const isLabelingNode = (node?: Record<string, any>): node is Node<LabelingNodeInfo, 'labeling'> => node?.type === 'labeling'
export const isModelNode = (node?: Record<string, any>): node is Node<ModelNodeInfo, 'train'> => node?.type === 'train'
export const isValidationNode = (node?: Record<string, any>): node is Node<ValidationNodeInfo, 'validation'> => node?.type === 'validation'
export const isInferenceNode = (node?: Record<string, any>): node is Node<InferenceNodeInfo, 'inference'> => node?.type === 'inference'
export const isImageMatchingNode = (node?: Record<string, any>): node is Node<ImageMatchingNodeInfo, 'imageMatching'> => node?.type === 'imageMatching'
export const isPreprocessingNode = (node?: Record<string, any>): node is Node<PreprocessingNodeInfo, 'preprocessing'> => node?.type === 'preprocessing'
export const isPreprocessingResultNode = (node?: Record<string, any>): node is Node<PreprocessingResultNodeInfo, 'preprocessingResult'> => node?.type === 'preprocessingResult'
export const isRoiNode = (node?: Record<string, any>): node is Node<RoiNodeInfo, 'roi'> => node?.type === 'roi'
export const isDataDriftNode = (node?: Record<string, any>): node is Node<DataDriftNodeInfo, 'dataDrift'> => node?.type === 'dataDrift'
export const isModelDriftNode = (node?: Record<string, any>): node is Node<ModelDriftNodeInfo, 'modelDrift'> => node?.type === 'modelDrift'
export const isModelPerformanceNode = (node?: Record<string, any>): node is Node<ModelPerformanceNodeInfo, 'modelPerformance'> => node?.type === 'modelPerformance'
export const isEdaNode = (node?: Record<string, any>): node is Node<EdaNodeInfo, 'eda'> => node?.type === 'eda'
export const isExternalModelNode = (node?: Record<string, any>): node is Node<ExternalModelNodeInfo, 'externalModel'> => node?.type === 'externalModel'
export const isMergeNode = (node?: Record<string, any>): node is Node<ModelPipelineMergeNodeInfo, 'modelPipelineMerge'> => node?.type === 'modelPipelineMerge'

export const isRunningNode = (node?: ReactFlowNode): boolean => {
  const status = (node?.data?.status || node?.data?.jobStatus) as TaskStatusValueType | undefined

  if (!status) {
    return false
  }
  return RUNNING_TASKS.includes(status)
}
/**
 * 노드 타입별 삭제 조건 검증
 * #### 데이터셋 노드
 * - 드리프트 레퍼런스 노드인 경우 삭제 불가
 * #### 라벨링 노드
 * - 재학습 파이프라인에 속할 경우 삭제 불가
 * - 드리프트 레퍼런스 노드인 경우 삭제 불가
 * #### 전처리 노드
 * - 후속 노드가 진행중이면 삭제 불가
 * */
export const isNodeDeletable = (node: ReactFlowNode, edges: ReactFlowEdge[]): boolean => {
  if (isRunningNode(node)) {
    return false
  }

  if (isDatasetNode(node)) {
    return !node.data.isUsedAsDriftReference
  }

  if (isLabelingNode(node)) {
    if (node.data.isAuto) {
      return false
    }
    if (node.data.isUsedAsDriftReference) {
      return false
    }
  }

  if (isPreprocessingNode(node)) {
    const storeNodes = store.getState().renewedPipeline.nodes
    const targetNodes = chain(edges)
      .filter((edge) => edge.source === node.id)
      .map((edge) => find(storeNodes, { id: edge.target }))
      .value()

    if (some(targetNodes, isRunningNode)) {
      return false
    }
  }

  return true
}
